import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguages } from '../../hooks/useLanguages';
import { loadPhrasebookAll } from '../../services/basicPhrasesV2';
import LanguageFlag from '../../components/LanguageFlag';
import { speakPhrase } from './phrasebookShared';

// One phrasebook entry across every supported language. Re-derives its data
// from the same cached _all.json PhrasebookPage already loaded (a second
// call is a cache hit, not a new request) rather than requiring router
// state, so this page also works from a direct link, a refresh, or the
// back/forward buttons — unlike the mobile app's PhrasebookPhraseScreen,
// which only ever receives translations already resolved by its caller.
export default function PhrasebookPhrasePage() {
  const { t } = useTranslation();
  const { phraseKey } = useParams();
  const { languages } = useLanguages();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [translations, setTranslations] = useState({});
  const [anchorText, setAnchorText] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    loadPhrasebookAll()
      .then((all) => {
        if (!active) return;
        const entry = all[phraseKey];
        if (!entry) { setError(true); setLoading(false); return; }
        const textByLang = entry.text || {};
        setTranslations(textByLang);
        setAnchorText(textByLang.en || Object.values(textByLang)[0] || phraseKey);
        setLoading(false);
      })
      .catch(() => { if (active) { setError(true); setLoading(false); } });
    return () => { active = false; };
  }, [phraseKey]);

  useEffect(() => {
    if (!anchorText) return undefined;
    const previousTitle = document.title;
    document.title = `${anchorText} | ${t('phrasebookPage.title')} | SublingoX`;
    return () => { document.title = previousTitle; };
  }, [anchorText, t]);

  const rows = languages.filter((lang) => translations[lang.code] != null);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <main className="mx-auto max-w-2xl">
        <Link to="/phrasebook" className="inline-flex items-center gap-2 text-sm font-black text-indigo-700 hover:text-indigo-900">
          <span aria-hidden="true">←</span> {t('phrasebookPage.title')}
        </Link>

        <h1 className="mt-4 mb-6 text-2xl font-black text-slate-900">{loading ? '…' : anchorText}</h1>

        {loading && (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((item) => <div key={item} className="h-16 animate-pulse rounded-2xl bg-slate-100" />)}
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <span className="text-5xl">🌐</span>
            <p className="mt-3 text-sm font-semibold text-slate-600">{t('phrasebookPage.unavailable')}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-2">
            {rows.map((lang) => (
              <div key={lang.code} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                <LanguageFlag flag={lang.flag} label={lang.label} className="h-6 w-8 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{lang.nativeLabel || lang.label}</p>
                  <p dir={lang.dir || 'ltr'} className="text-base font-bold text-slate-900">{translations[lang.code]}</p>
                </div>
                <button
                  type="button"
                  onClick={() => speakPhrase(lang.tts, translations[lang.code])}
                  aria-label={t('phrasebookPage.listen')}
                  className="shrink-0 rounded-full p-1.5 text-indigo-600 hover:bg-indigo-50"
                >
                  🔊
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
